import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Grid,
  useTheme,
  useMediaQuery,
  Tooltip,
  Chip
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import AddMonitorIcon from '@mui/icons-material/MonitorWeight';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

import api from '../api/api';



  const initialFormState = {
    peso: '',
    temperatura: '',
    fecha_registro: dayjs(),
    observaciones: '',
  };
const ControlPesoModal = ({
  visible,
  onClose,
  pacienteId,
  onSave,
  nombrePaciente = 'Paciente',
  currentUserId, // id_usuario
  idSucursal,    // id_sucursal
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [sucursalAuto, setSucursalAuto] = useState(null);
  const [nombreSucursalAuto, setNombreSucursalAuto] = useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);


    const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      // Llamamos al endpoint mejorado (que ya trae UNION con recetas)
      const res = await api.get(`/controles-peso/por-paciente/${pacienteId}`);
      // El backend ya los devuelve ordenados por fecha
      setHistory(res.data || []);
    } catch (err) {
      console.error("Error historial:", err);
      setError('No se pudo cargar el historial unificado');
    } finally {
      setLoadingHistory(false);
    }
  }, [pacienteId]);

  const resetForm = useCallback(() => {
  setFormData(initialFormState);
  setEditingId(null);
  setError(null);
}, []);



  // Cargar historial al abrir o cambiar de paciente
  useEffect(() => {
    if (visible && pacienteId) {
      resetForm();
      loadHistory();
    }
  }, [visible, pacienteId, loadHistory, resetForm]);

  useEffect(() => {
    const fetchSucursalNombre = async (sId) => {
      try {
        const res = await api.get('/sucursales/');
        const sucursalActiva = res.data.find(s => String(s.id_sucursal) === String(sId));
        if (sucursalActiva) {
          setNombreSucursalAuto(sucursalActiva.nombre_sucursal);
        }
      } catch (err) {
        console.error("Error al obtener nombres de sucursales:", err);
      }
    };

    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const sId = payload.id_sucursal || payload.sucursal_id || payload.idSucursal || null;
        if (sId) {
          setSucursalAuto(sId);
          fetchSucursalNombre(sId); // Buscamos el nombre una vez tenemos el ID
        }
      } catch (err) {
        console.error('Error al leer sucursal del token:', err);
      }
    }
  }, []);




  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (item) => {
  if (item.esReceta) return; 

  setFormData({
    peso: item.peso?.toString() || '',
    temperatura: item.temperatura?.toString() || '',
    fecha_registro: dayjs(item.fecha_registro),
    observaciones: item.observaciones || '',
  });
  
  // Debe coincidir con el uniqueId de la tabla
  setEditingId(item.id_original || item.id_control_peso); 
  setError(null);
};

  const handleSubmit = async () => {
    if (!formData.peso || parseFloat(formData.peso) <= 0) {
      setError('El peso es obligatorio y debe ser mayor a 0');
      return;
    }
    const sIdFinal = sucursalAuto || idSucursal;

    setLoading(true);
    setError(null);

    const payload = {
      id_paciente: pacienteId,
      id_usuario: currentUserId,
      id_sucursal: sIdFinal ? parseInt(sIdFinal) : null,
      peso: parseFloat(formData.peso),
      temperatura: formData.temperatura ? parseFloat(formData.temperatura) : null,
      fecha_registro: formData.fecha_registro.format('YYYY-MM-DD HH:mm:ss'),
      observaciones: formData.observaciones.trim() || null,
    };

    console.log("DEBUG: Sucursal de Prop:", idSucursal);
console.log("DEBUG: Sucursal de Token:", sucursalAuto);
console.log("DEBUG: Enviando al servidor ID Sucursal:", idSucursal || sucursalAuto);


    try {
      if (editingId) {
        // Verifica que la URL sea la correcta para actualizar
        await api.put(`/controles-peso/${editingId}`, payload);
      } else {
        await api.post('/controles-peso/', payload);
      }

      resetForm();
      
      // LIMPIEZA POST-GUARDADO
      setFormData(initialFormState);
      setEditingId(null); // Fundamental resetear esto aquí
      await loadHistory();
      onSave?.(); 
     // Notifica al componente padre que hubo cambios
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
  try {
    await api.delete(`/controles-peso/${deleteId}`);
    
    // En lugar de filtrar localmente, recargamos para asegurar sincronía con DB
    await loadHistory(); 
    
    if (editingId === deleteId) {
      setFormData(initialFormState);
      setEditingId(null);
    }
    setDeleteDialogOpen(false);
    onSave?.(); // Avisar al padre
  } catch (err) {
    setError("Error al eliminar el registro");
  }
};


  return (
    <Dialog 
      open={visible} 
      onClose={() => onClose(false)} 
      maxWidth="lg" 
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddMonitorIcon />
          <Typography variant="h6">Control de Peso - {nombrePaciente}</Typography>
        </Box>
        {(nombreSucursalAuto) && (
              <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                Se registrara en: <b>{nombreSucursalAuto.toUpperCase()}</b> (ID: {sucursalAuto})
              </Typography>
            )}
        
        <Button onClick={() => onClose(false)} sx={{ color: 'white', fontWeight: 'bold' }}>CERRAR VENTANA</Button>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Grid container sx={{ minHeight: '500px' }}>
          
          {/* COLUMNA FORMULARIO */}
          <Grid item xs={12} md={4} sx={{ p: 3, borderRight: { md: '1px solid #e0e0e0' } }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold', textTransform: 'uppercase' }}>
              {editingId ? 'Editando Registro' : 'Nueva Entrada'}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <TextField
                  label="Peso (Lb)"
                  name="peso"
                  type="number"
                  fullWidth
                  required
                  value={formData.peso}
                  onChange={handleChange}
                  variant="outlined"
                  inputProps={{ step: '0.01' }}
                />
                <TextField
                  label="Temperatura (°C)"
                  name="temperatura"
                  type="number"
                  fullWidth
                  value={formData.temperatura}
                  onChange={handleChange}
                  variant="outlined"
                  inputProps={{ step: '0.1' }}
                />
                <DateTimePicker
                  label="Fecha del Pesaje"
                  value={formData.fecha_registro}
                  onChange={(val) => setFormData({...formData, fecha_registro: val})}
                  sx={{ width: '100%' }}
                  ampm={true}
                />
                <TextField
                  label="Notas Adicionales"
                  name="observaciones"
                  multiline
                  rows={4}
                  fullWidth
                  value={formData.observaciones}
                  onChange={handleChange}
                  placeholder="Ej: Ayunas, después de cirugía..."
                />
                
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
  <Button 
    variant="contained" 
    fullWidth 
    size="large"
    onClick={handleSubmit}
    disabled={loading || !formData.peso}
  >
    {loading ? <CircularProgress size={24} /> : editingId ? 'ACTUALIZAR' : 'GUARDAR REGISTRO'}
  </Button>
  
  {editingId && (
    <Tooltip title="Cancelar edición">
      <Button 
        variant="outlined" 
        color="error" // Cambiado a error (rojo) para que sea más intuitivo
        onClick={() => { 
          setEditingId(null); 
          setFormData(initialFormState);
          setError(null); // Limpiamos también el error si existía uno de la edición fallida
        }}
      >
        X
      </Button>
    </Tooltip>
  )}
</Box>
              </Box>
            </LocalizationProvider>
          </Grid>

          {/* COLUMNA HISTORIAL */}
          <Grid item xs={12} md={8} sx={{ bgcolor: '#fbfbfb', p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
              <HistoryIcon color="action" />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Historial del Paciente</Typography>
            </Box>

            {loadingHistory ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: '420px', border: '1px solid #e0e0e0' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#f0f0f0', fontWeight: 'bold' }}>Fecha</TableCell>
                      <TableCell sx={{ bgcolor: '#f0f0f0', fontWeight: 'bold' }} align="right">Peso</TableCell>
                      <TableCell sx={{ bgcolor: '#f0f0f0', fontWeight: 'bold' }} align="right">Temp.</TableCell>
                      <TableCell sx={{ bgcolor: '#f0f0f0', fontWeight: 'bold' }}>Obs.</TableCell>
                      <TableCell sx={{ bgcolor: '#f0f0f0', fontWeight: 'bold' }} align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((row) => {
                      const isReadOnly = row.esReceta;
                     const uniqueId = row.id_original || row.id_control_peso || `temp-${row.fecha_registro}`; // Flag enviado por el backend mejorado

  return (
    <TableRow 
  key={isReadOnly ? `${row.tipo}-${uniqueId}` : `peso-${uniqueId}`}
  hover 
  selected={editingId === uniqueId}
  sx={{ 
    '&.Mui-selected': { bgcolor: '#e3f2fd' },
    // Añadimos una barrita de color a la izquierda según el tipo
    borderLeft: isReadOnly 
      ? `4px solid ${row.tipo === 'receta' ? theme.palette.secondary.main : theme.palette.info.main}` 
      : '4px solid transparent',
    bgcolor: isReadOnly ? 'rgba(0, 0, 0, 0.01)' : 'inherit'
  }}
>
      <TableCell>{dayjs(row.fecha_registro).format('DD/MM/YYYY hh:mm A')}</TableCell>
      <TableCell align="right">
        <Typography variant="body2" sx={{ fontWeight: isReadOnly ? 'bold' : 'normal' }}>
          {row.peso} Lb
        </Typography>
      </TableCell>
      <TableCell align="right">{row.temperatura ? `${row.temperatura}°C` : '-'}</TableCell>
      <TableCell sx={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {isReadOnly ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Chip 
        label={row.origen || 'Dato Externo'} 
        size="small" 
        variant="filled"
        // Asignamos color según el tipo que viene del backend
        color={row.tipo === 'receta' ? "secondary" : "info"} 
        sx={{ 
          height: 18, 
          fontSize: '0.6rem', 
          fontWeight: 'bold',
          width: 'fit-content'
        }} 
      />
      <Typography 
        variant="caption" 
        sx={{ 
          display: 'block', 
          whiteSpace: 'nowrap', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis',
          color: 'text.secondary',
          fontStyle: 'italic'
        }}
      >
        {row.observaciones}
      </Typography>
    </Box>
  ) : (
    <Tooltip title={row.observaciones || 'Sin observaciones'}>
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {row.observaciones || '-'}
      </Typography>
    </Tooltip>
  )}
</TableCell>
      <TableCell align="center">
        {!isReadOnly ? (
          <>
            <IconButton size="small" onClick={() => handleEdit(row)} color="primary">
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => { 
                    setDeleteId(uniqueId); 
                setDeleteDialogOpen(true);
                  }} 
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
          </>
        ) : (
          <Typography variant="caption" color="text.secondary">Solo lectura</Typography>
        )}
      </TableCell>
    </TableRow>
  );
})}
                    {history.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No hay registros previos para este paciente.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      {/* DIÁLOGO ELIMINAR */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>¿Eliminar este registro?</DialogTitle>
        <DialogContent>Esta acción no se puede deshacer.</DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, gap: 1 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Confirmar</Button>
        </Box>
      </Dialog>
    </Dialog>
  );
};

export default ControlPesoModal;